import React, { useEffect, useRef, useState } from 'react';
import './Apresentacao.css';

/* ── dados da timeline ─────────────────────────────────────────────────────── */
const TIMELINE = [
  {
    id: 'coleta_realtime',
    phase: '01',
    label: 'Coleta em Tempo Real',
    icon: '🔎',
    color: '#00a8ff',
    title: 'Direto da Fonte de Dados',
    desc: 'Não vendemos listas recicladas de 2 anos atrás. Nossos robôs varrem o Google Maps e a web no exato momento, garantindo que a empresa está de portas abertas hoje.',
    metrics: [
      { value: '100%', label: 'Dados Frescos' },
      { value: 'Zero', label: 'Empresas Fechadas' },
      { value: 'Maps', label: 'Fonte Oficial' },
    ],
    code: `STATUS: BUSCANDO
Segmento: Clínicas
Local: São Paulo, SP
Resultado: Encontrados 5.230 registros...`
  },
  {
    id: 'higienizacao',
    phase: '02',
    label: 'Higienização Ativa',
    icon: '🧼',
    color: '#00f5c4',
    title: 'Filtro Anti-Lixo',
    desc: 'Sua equipe perde 40% do tempo ligando para telefones de contabilidade ou números que não existem. Nossa inteligência remove ativamente esses contatos antes de chegar a você.',
    metrics: [
      { value: 'WhatsApp', label: 'Validação' },
      { value: 'Celular', label: 'Prioridade' },
      { value: '100%', label: 'Sem Duplicatas' },
    ],
    code: `ANALISANDO CONTATOS
Removendo: (11) 3333-0000 (Fixo Inativo)
Removendo: contato@contabilidade.com
Validando: (11) 99999-9999 (WhatsApp Ativo) ✔️`,
  },
  {
    id: 'enriquecimento',
    phase: '03',
    label: 'Enriquecimento Profundo',
    icon: '⚡',
    color: '#7c3aed',
    title: 'CNPJ, Sócios e E-mails (OSINT)',
    desc: 'Um lead com apenas telefone é frio. O Agente 2 captura CNPJ, Capital Social e Sócios. O Agente 3 faz busca avançada (OSINT) cruzando sites para achar e-mails corporativos diretos dos decisores.',
    metrics: [
      { value: 'OSINT', label: 'E-mails de Sócios' },
      { value: 'Receita', label: 'CNPJ e Capital' },
      { value: 'Website', label: 'Verificado' },
    ],
    code: `AGENTE 2 & 3 EM AÇÃO
[+] CNPJ: 12.345... (R$ 500k Capital)
[+] Sócio: João Silva
[+] OSINT: joao.silva@clinica.com
[+] Qualidade: ALTA ⭐⭐⭐⭐⭐`,
  },
  {
    id: 'inteligencia',
    phase: '04',
    label: 'Inteligência de Mercado',
    icon: '🧠',
    color: '#f43f5e',
    title: 'Market Share & IA Analítica',
    desc: 'Compare quantos leads existem no mercado real (dados do IBGE/CEMPRE) vs. quantos estão na nossa base. O Agente 4 (Google Gemini) analisa as empresas e gera um Lead Score automático de conversão.',
    metrics: [
      { value: 'IBGE', label: 'Tamanho do Mercado' },
      { value: 'Gemini', label: 'Score Analítico' },
      { value: 'FOMO', label: 'Vantagem Competitiva' },
    ],
    code: `AGENTE 4: DOSSIÊ DE MERCADO
Mercado SP: 50.000 clínicas ativas
Mapeado: 3.200 validadas (Pronto)
Lead Score: A (Hot Lead)
Conclusão: Alta conversão esperada.`,
  },
  {
    id: 'transparencia',
    phase: '05',
    label: 'Transparência Total',
    icon: '🛡️',
    color: '#f59e0b',
    title: 'Você no Controle',
    desc: 'Aqui você não compra no escuro. Mostramos o nome da empresa, o segmento e a avaliação dela ANTES de você decidir comprar. Pague apenas pelos leads que fizerem sentido.',
    metrics: [
      { value: 'Visível', label: 'Nome e Avaliação' },
      { value: 'Parcial', label: 'Contatos Mascarados' },
      { value: 'Flexível', label: 'Escolha a dedo' },
    ],
    code: `PRÉVIA DO LEAD
Nome: Clínica Odonto Prime
Avaliação: 4.8 ⭐ (120 reviews)
Contato: (11) 9822... [Bloqueado]
E-mail: dr... [Bloqueado]`,
  },
  {
    id: 'crm',
    phase: '06',
    label: 'Pronto para Fechar',
    icon: '🚀',
    color: '#10b981',
    title: 'Exportação Imediata',
    desc: 'Esqueça horas formatando planilhas. Em um clique, seus leads são liberados em um formato limpo, prontos para serem importados no seu CRM (RD Station, Pipedrive, etc) e sua equipe começar a vender.',
    metrics: [
      { value: 'Imediato', label: 'Download' },
      { value: 'CSV/Excel', label: 'Formatos limpos' },
      { value: 'Vendas', label: 'Foco no fechamento' },
    ],
    code: `LIBERAÇÃO CONCLUÍDA
- 500 Leads Desbloqueados
- Formato: Estruturado Padrão CRM
- Status: Download Disponível ⬇️`,
  },
];

/* ── componente principal ──────────────────────────────────────────────────── */
export default function Apresentacao() {
  const [activeIdx, setActiveIdx] = useState(null);
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [particles, setParticles] = useState([]);
  const itemRefs = useRef([]);
  const containerRef = useRef(null);

  /* gera partículas de fundo */
  useEffect(() => {
    const pts = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      speed: Math.random() * 20 + 10,
      delay: Math.random() * 5,
      opacity: Math.random() * 0.5 + 0.1,
    }));
    setParticles(pts);
  }, []);

  /* intersection observer para animar ao scroll */
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisibleItems((prev) => new Set(prev).add(e.target.dataset.idx));
          }
        });
      },
      { threshold: 0.25 }
    );
    itemRefs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="apres-root" ref={containerRef}>
      {/* partículas de fundo */}
      <div className="apres-particles" aria-hidden="true">
        {particles.map((p) => (
          <span
            key={p.id}
            className="apres-particle"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: p.size,
              height: p.size,
              opacity: p.opacity,
              animationDuration: `${p.speed}s`,
              animationDelay: `${p.delay}s`,
            }}
          />
        ))}
      </div>

      {/* grid aurora */}
      <div className="apres-aurora" aria-hidden="true">
        <div className="aurora-orb aurora-orb--1" />
        <div className="aurora-orb aurora-orb--2" />
        <div className="aurora-orb aurora-orb--3" />
      </div>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <header className="apres-hero">
        <div className="apres-hero__badge">
          <span className="badge-dot" />
          SaaS · B2B Lead Intelligence Platform
        </div>
        <h1 className="apres-hero__title text-shimmer">
          <span className="title-word title-word--maps">LeadsZone</span>
          <span className="title-word title-word--pro">Maps</span>
        </h1>
        <p className="apres-hero__sub">
          Pipeline completo de geração e comercialização de leads B2B —<br />
          do scraping inteligente ao desbloqueio por crédito.
        </p>

        {/* métricas rápidas */}
        <div className="apres-hero__stats">
          {[
            { n: '1.523', u: 'Leads únicos' },
            { n: '6', u: 'Fases de pipeline' },
            { n: '< 2s', u: 'Por coleta' },
            { n: 'R$75', u: 'Entrada mínima' },
          ].map((s, i) => (
            <div key={i} className="hero-stat" style={{ '--i': i }}>
              <strong>{s.n}</strong>
              <span>{s.u}</span>
            </div>
          ))}
        </div>

        <div className="apres-hero__scroll-hint">
          <span>Scroll para explorar</span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 4v12M5 11l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </header>

      {/* ── TIMELINE ─────────────────────────────────────────────────────── */}
      <section className="apres-timeline" aria-label="Pipeline de fases">
        {/* linha central */}
        <div className="timeline-spine" aria-hidden="true" />

        {TIMELINE.map((item, i) => {
          const isLeft = i % 2 === 0;
          const isVisible = visibleItems.has(String(i));
          const isActive = activeIdx === i;

          return (
            <article
              key={item.id}
              ref={(el) => (itemRefs.current[i] = el)}
              data-idx={i}
              className={[
                'timeline-item',
                isLeft ? 'timeline-item--left' : 'timeline-item--right',
                isVisible ? 'timeline-item--visible' : '',
                isActive ? 'timeline-item--active' : '',
              ].join(' ')}
              style={{ '--color': item.color, '--delay': `${i * 0.1}s` }}
              onClick={() => setActiveIdx(isActive ? null : i)}
            >
              {/* nó central */}
              <div className="timeline-node" aria-hidden="true">
                <div className="node-ring" />
                <div className="node-dot">{item.icon}</div>
                <div className="node-pulse" />
              </div>

              {/* card */}
              <div className="timeline-card">
                <div className="card-phase">{item.phase}</div>
                <div className="card-header">
                  <span className="card-icon">{item.icon}</span>
                  <div>
                    <div className="card-label">{item.label}</div>
                    <h2 className="card-title">{item.title}</h2>
                  </div>
                </div>

                <p className="card-desc">{item.desc}</p>

                {/* métricas */}
                <div className="card-metrics">
                  {item.metrics.map((m, mi) => (
                    <div key={mi} className="metric" style={{ '--mi': mi }}>
                      <strong>{m.value}</strong>
                      <span>{m.label}</span>
                    </div>
                  ))}
                </div>

                {/* código — expande ao clicar */}
                <div className={`card-code-wrap ${isActive ? 'card-code-wrap--open' : ''}`}>
                  <pre className="card-code"><code>{item.code}</code></pre>
                </div>

                <button className="card-toggle" aria-expanded={isActive}>
                  {isActive ? 'Fechar código ▲' : 'Ver implementação ▼'}
                </button>
              </div>
            </article>
          );
        })}
      </section>





      {/* ── FOOTER ──────────────────────────────────────────────────────── */}
      <footer className="apres-footer">
        <div className="footer-glow" />
        <p>LeadsZone Maps · SaaS Blueprint · Antigravity IDE</p>
        <div className="footer-pills">
          <span>Fase 1 ✅</span>
          <span>Fase 2 ✅</span>
          <span>Fase 3 🔄</span>
          <span>Fase 4 ⏳</span>
        </div>
      </footer>
    </div>
  );
}
